import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToggle } from 'react-use';
import { Common } from '@thecvlb/design-system/lib/src';
import classNames from 'classnames';
import dayjs from 'dayjs';

import { useGetInsuranceProvidersQuery } from 'services/insurance/insurance';

import { selectOrchestrate } from 'store';
import { OrchestrateState } from 'store/orchestrate-signup/orchestrate.types';

import BenefitsCheckResultsCard from 'features/BenefitsCheckResultsCard';
import CancelOnboarding from 'modals/CancelOnboarding';
import Loader from 'shared/Loader';
import { notifyError } from 'shared/Toast/Toast';
import { FormValues } from 'widgets/Insurance/insurance.types';
import AddInsuranceForm from 'widgets/signUp/AddInsuranceForm';

import { useAppSelector, useQuery } from 'hooks';
import useSubmitOrchestrateForm from 'hooks/useSubmitOrchestrateForm';
import { DateFormat, FlowTypes, PathName } from 'utils/enums';
import { getMappedInsuranceOptions } from 'utils/helpers';
import { handlePostHogEvent } from 'utils/posthog';

import Heading from '../../components/Heading';
import UploadInsurance from '../UploadInsurance';

interface AddInsuranceProps {
  onContinue: (willUseInsurance?: boolean) => void;
  selectedFlow?: FlowTypes;
}

const AddInsurance: React.FC<AddInsuranceProps> = ({ onContinue, selectedFlow }) => {
  const navigate = useNavigate();
  const stepFromQuery = useQuery().get('step') ?? '';
  const [notifyOnChange, toggleNotifyOnChange] = useToggle(true);
  const [cancelPopup, toggleCancelPopup] = useToggle(false);
  const [step, setStep] = useState<'initial' | 'processing' | 'results' | ''>('initial');
  const [docIDs, setDocIDs] = useState<string[]>([]);

  const { isLoading, send } = useSubmitOrchestrateForm();
  const {
    user: { firstName, lastName, dob, accessToken, state },
    mif_qa: mifQa,
    insurances
  } = useAppSelector(selectOrchestrate);

  const { data, isFetching: isFetchingProviders } = useGetInsuranceProvidersQuery({
    accessToken,
    state
  });

  const mappedOptions = getMappedInsuranceOptions(data, isFetchingProviders);

  const primaryInsuranceData = insurances?.[0]?.eligibilityResponse;
  const benefitsCheckStatus = primaryInsuranceData?.status;

  const onSubmit = (
    v: (FormValues & { isPrimaryHolder: boolean; payerId: string; provider: string })[]
  ) => {
    if (docIDs.length < 2) {
      window.scrollTo({ behavior: 'smooth', top: 0 });
      return notifyError('Please upload both front and back of your insurance card.');
    }
    const notifyQuestion = mifQa.find((q) => q.question === 'notify_on_change_coverage');
    if (notifyQuestion) {
      toggleNotifyOnChange(Boolean(notifyQuestion.answer));
    }
    const isCheckingForPrescription = stepFromQuery === 'check-medication-coverage';
    const body: OrchestrateState['insurances'] = v.map((i, index) => ({
      ...i,
      rank: index + 1,
      ...(index === 0 && { isNeedVerify: true }),
      discoveredByNirvana: false,
      insuranceDocuments: docIDs
    }));
    if (!isCheckingForPrescription) {
      setStep('processing');
    }
    const shouldShowBenefitsCheckResults =
      selectedFlow === FlowTypes.WeightManagementFlowInsuranceAlt
        ? true
        : selectedFlow === FlowTypes.WeightManagementFlowInsurance
          ? !isCheckingForPrescription
          : false;
    handlePostHogEvent('wm-el-initiate');
    send(
      'insurances',
      body,
      () => {
        if (!shouldShowBenefitsCheckResults) {
          return onContinue();
        } else {
          setStep('results');
        }
      },
      undefined,
      () => {
        setStep('initial');
      }
    );
  };

  const headingContent = {
    initial: {
      subtitle:
        'Please enter your insurance information below. We will quickly verify your benefits to determine how they align with our services, moving you one step closer to personalized care.',
      title: 'Add your insurance'
    },
    processing: {
      subtitle: `Hang tight! We're currently checking your insurance benefits to see how they align with our services. This process may take a few moments, but we're working swiftly to bring you closer to personalized care.`,
      title: 'Checking insurance'
    },
    results: {
      subtitle:
        !!benefitsCheckStatus && ['ACTIVE', 'ACTIVE_AT_RISK'].includes(benefitsCheckStatus)
          ? 'Your insurance has been successfully verified and will cover the cost of your upcoming appointment.'
          : `Unfortunately we're unable to ${benefitsCheckStatus === 'INACTIVE' ? 'accept' : 'verify'} your insurance at this time. But don't worry, you can still proceed with your appointment without insurance.`,
      title: 'Your benefits'
    }
  };

  const handleProceed = (value: boolean | null) => () => {
    if (value === null) {
      toggleCancelPopup();
    } else {
      send(
        'mif_qa',
        [
          { answer: value, question: 'will_use_insurance_for_medications' },
          ...(benefitsCheckStatus === 'INACTIVE'
            ? [
                {
                  answer: notifyOnChange,
                  question: 'notify_on_change_coverage'
                }
              ]
            : [])
        ],
        () => {
          onContinue(value);
        }
      );
    }
  };

  const content = {
    initial: (
      <>
        <UploadInsurance accessToken={accessToken} onUpdateFile={setDocIDs} />
        <div className="flex flex-col gap-4">
          <h3 className="font-bold text-primary-700">Your information</h3>
          <div className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-gray-100 p-4">
            <div className="flex flex-col gap-1 text-mSm md:text-sm">
              <span className="font-bold">Legal name</span>
              <span>{`${firstName} ${lastName}`}</span>
            </div>
            <div className="flex flex-col gap-1 text-mSm md:text-sm">
              <span className="font-bold">Date of birth</span>
              <span>{dayjs(dob).format(DateFormat.MMMM_DD_YYYY)}</span>
            </div>
          </div>
        </div>
        <AddInsuranceForm
          defaultValues={insurances}
          loading={isLoading}
          providers={mappedOptions}
          onSkip={handleProceed(false)}
          onSubmit={onSubmit}
        />
      </>
    ),
    processing: <Loader isVisible />,
    results: (
      <>
        <BenefitsCheckResultsCard
          data={primaryInsuranceData}
          insurancesAmount={insurances.length}
          notifyOnChange={notifyOnChange}
          provider={insurances[0]?.provider || ''}
          toggleNotifyOnChange={toggleNotifyOnChange}
        />
        <div className="flex flex-col items-center justify-center gap-x-3 gap-y-2 max-md:mt-auto max-md:flex-col">
          {!!benefitsCheckStatus && ['ACTIVE', 'ACTIVE_AT_RISK'].includes(benefitsCheckStatus) ? (
            <>
              <Common.Button
                color="blue"
                disabled={isLoading}
                fullWidthOnMobile
                onClick={handleProceed(true)}
              >
                Continue
              </Common.Button>
              <Common.Button disabled={isLoading} fullWidthOnMobile onClick={handleProceed(false)}>
                Pay without insurance
              </Common.Button>
            </>
          ) : (
            <>
              <CancelOnboarding
                isOpen={cancelPopup}
                onClose={() => toggleCancelPopup(false)}
                onConfirm={() => navigate(PathName.Login)}
              />
              <Common.Button
                color="blue"
                disabled={isLoading}
                isLoading={isLoading}
                fullWidthOnMobile
                onClick={handleProceed(false)}
              >
                Pay without insurance
              </Common.Button>
              <Common.Button fullWidthOnMobile onClick={handleProceed(null)}>
                Cancel
              </Common.Button>
            </>
          )}
        </div>
      </>
    )
  };

  return (
    <>
      {!!step && (
        <div
          className={classNames(
            'mx-auto flex max-w-[500px] flex-col max-md:h-full',
            step === 'results' ? 'gap-4' : 'gap-6'
          )}
        >
          <Heading
            category="Plan"
            subtitle={
              <span className="align-top text-mSm md:text-base">
                {headingContent[step].subtitle}
              </span>
            }
            title={headingContent[step].title}
          />
          {content[step]}
        </div>
      )}
    </>
  );
};

export default AddInsurance;
